import { zodResolver } from "@hookform/resolvers/zod";
import { url } from "@mozioinc/utils";
import React, { useMemo, useState } from "react";
import { useForm, useController } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";
import { z } from "zod";
import { forwardRef } from "../utils/ref";
import { getUrlByLocale, URL_NAMES } from "../utils/translation";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Combobox } from "./combobox";
import { Input } from "./input";
import { Link } from "./link";
import { Select } from "./select";
import type { CheckboxProps } from "./checkbox";
import type { ComboboxProps } from "./combobox";
import type { TransNamespace } from "../types";
import type { ReactElement } from "react";

const purchaseEsimSchema = z.object({
  country: z.string().min(1),
  plan: z.string().min(1),
  deviceConfirmation: z.literal(true),
  termsAndServices: z.literal(true),
  understandRefund: z.literal(true),
});

export type PurchaseFormInputs = z.infer<typeof purchaseEsimSchema>;

export type PurchaseFormProps = {
  countryFieldProps: {
    countries: string[];
    loading: boolean;
    onChange?: (value: string) => void;
    value: string;
  };
  planFieldProps: {
    plans: {
      value: string;
      dataGb: string;
      validityDays: number;
      price: string;
    }[];
    loading: boolean;
    value: string;
    onChange?: (value: string) => void;
  };
  couponFieldProps: {
    finalPrice?: string;
    discount?: string;
    isValid?: boolean;
    isValidated?: boolean;
    onValidation?: (coupon: string) => void;
  };
  countryInputRef?: React.ForwardedRef<HTMLInputElement>;
  purchaseLoading: boolean;
  onSubmit?: (inputs: PurchaseFormInputs) => void;
  onCouponValidation?: (coupon: string) => void;
};

export const PurchaseForm = (props: PurchaseFormProps) => {
  const {
    countryFieldProps,
    planFieldProps,
    couponFieldProps,
    countryInputRef,
    purchaseLoading,
    onSubmit = () => {},
  } = props;

  const { t, i18n } = useTranslation<TransNamespace>("esim:purchase-form");
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PurchaseFormInputs>({
    resolver: zodResolver(purchaseEsimSchema),
  });

  const countryField = useController({
    name: "country",
    control,
    defaultValue: "",
  });
  const planField = useController({
    name: "plan",
    control,
    defaultValue: "",
  });
  const deviceConfirmationField = useController({
    name: "deviceConfirmation",
    control,
  });
  const termsAndServicesField = useController({
    name: "termsAndServices",
    control,
  });
  const understandRefundField = useController({
    name: "understandRefund",
    control,
  });

  if (countryField.field.value !== countryFieldProps.value) {
    countryField.field.onChange(countryFieldProps.value);
  }

  if (planField.field.value !== planFieldProps.value) {
    planField.field.onChange(planFieldProps.value);
  }

  const handleCountryInputRef = (ref: HTMLInputElement) => {
    countryField.field.ref(ref);
    forwardRef(countryInputRef)(ref);
  };

  const selectedPlanPrice =
    planFieldProps.plans.find((p) => p.value === planField.field.value)
      ?.price ?? "";

  const planOptions = planFieldProps.plans.map((p) => ({
    value: p.value,
    content: (
      <div className="flex justify-between">
        {t("{{dataGb}} - valid for {{validityDays}} days", {
          dataGb: p.dataGb,
          validityDays: p.validityDays,
        })}
        <div className="text-primary">{p.price}</div>
      </div>
    ),
  }));

  return (
    <form
      className="flex max-w-md flex-col gap-7 rounded-md bg-black/70 px-3 pb-5 pt-4 text-white md:gap-9 md:px-8 md:pb-12 md:pt-5"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col gap-4">
        <h1 className="text-center text-xl">
          {t("Get mobile internet at local prices. Anywhere in the world!")}
        </h1>
        <p className="text-center text-sm">
          {t(
            "Use our eSIM for cheap internet access anywhere in the world without changing your phone number.",
          )}
        </p>
      </div>

      <div className="flex max-w-xs flex-col gap-5 self-center md:gap-6">
        <div>
          <ComboboxWithFilter
            className="w-full"
            label={t("Where are you traveling?")}
            placeholder={t("Country...")}
            openOnFocus
            options={countryFieldProps.countries}
            loading={countryFieldProps.loading}
            noOptionsText={t("No matching countries found")}
            value={countryField.field.value}
            onChange={countryFieldProps.onChange}
            inputRef={handleCountryInputRef}
            name={countryField.field.name}
            onBlur={countryField.field.onBlur}
          />
          {errors.country && (
            <ErrorMessage>
              {t("Please, choose the destination country!")}
            </ErrorMessage>
          )}
        </div>

        <div>
          <Select
            className="w-full"
            label={t("Choose your Data Plan?")}
            placeholder={t("Plan...")}
            options={planOptions}
            noOptionsText={t("No eSIM plans")}
            loading={planFieldProps.loading}
            value={planField.field.value}
            name={planField.field.name}
            onChange={planFieldProps.onChange}
            onBlur={planField.field.onBlur}
          />
          {errors.plan && (
            <ErrorMessage>{t("Please, choose a plan!")}</ErrorMessage>
          )}
        </div>

        <div>
          <Input
            label={t("Promo code")}
            placeholder={t("Enter a valid promo code")}
            onBlur={(event) =>
              couponFieldProps?.onValidation &&
              couponFieldProps.onValidation(event.target.value)
            }
            rightContent={
              couponFieldProps?.isValidated ? (
                couponFieldProps.isValid ? (
                  <div className="absolute right-4 flex items-center gap-1 text-sm text-green-500">
                    <div>{couponFieldProps.discount}</div>
                    <FaRegCheckCircle />
                  </div>
                ) : (
                  <div className="absolute right-4 flex items-center gap-1 text-sm text-red-500">
                    <div>invalid code</div>
                    <FaRegTimesCircle />
                  </div>
                )
              ) : null
            }
          />
        </div>

        <div>
          <CheckboxField
            id="purchaseEsimForm-deviceConfirmation"
            labelHref={getUrlByLocale(
              URL_NAMES.COMPATIBLE_DEVICE,
              i18n.language,
            )}
            labelContent={
              <Trans
                i18nKey="I confirm that my device is in the list of <a>compatible devices</a>"
                t={t}
              />
            }
            checked={deviceConfirmationField.field.value}
            onChange={deviceConfirmationField.field.onChange}
            onBlur={deviceConfirmationField.field.onBlur}
            name={deviceConfirmationField.field.name}
          />
          {errors.deviceConfirmation && (
            <ErrorMessage>
              {t("Please, confirm that your device is compatible!")}
            </ErrorMessage>
          )}
        </div>

        <div>
          <CheckboxField
            id="purchaseEsimForm-termsAndServices"
            labelHref={url.setPathname("/esim/terms-of-service")}
            labelContent={
              <Trans
                i18nKey="I agree with the <a>Terms and Conditions</a>"
                t={t}
              />
            }
            checked={termsAndServicesField.field.value}
            onChange={termsAndServicesField.field.onChange}
            onBlur={termsAndServicesField.field.onBlur}
            name={termsAndServicesField.field.name}
          />
          {errors.termsAndServices && (
            <ErrorMessage>
              {t("Please, accept the Terms and Conditions!")}
            </ErrorMessage>
          )}
        </div>

        <div>
          <CheckboxField
            id="purchaseEsimForm-understandRefund"
            labelContent={
              <Trans
                i18nKey="I understand that refunds for eSIM are only provided for technical issues"
                t={t}
              />
            }
            checked={understandRefundField.field.value}
            onChange={understandRefundField.field.onChange}
            onBlur={understandRefundField.field.onBlur}
            name={understandRefundField.field.name}
          />
          {errors.understandRefund && (
            <ErrorMessage>
              {t(
                "Please, accept that refunds are only provided for technical issues!",
              )}
            </ErrorMessage>
          )}
        </div>

        <Button
          className="mt-4 grid grid-cols-[auto_1fr_auto] font-medium"
          color="secondary"
          type="submit"
          loading={purchaseLoading}
        >
          <span>{t("Purchase")}</span>
          <span className="text-right">
            {couponFieldProps.finalPrice ?? selectedPlanPrice}
          </span>
        </Button>

        <Link
          className="justify-self-center text-center"
          color="primary"
          href={getUrlByLocale(URL_NAMES.LEARN_ESIM, i18n.language)}
        >
          {t("Learn how our eSIM works")}
        </Link>
      </div>
    </form>
  );
};

const ComboboxWithFilter = (props: Omit<ComboboxProps, "onInputChange">) => {
  const { options, ...others } = props;
  const [inputValue, setInputValue] = useState("");
  const filteredOptions = useMemo(
    () =>
      inputValue.length === 0
        ? options
        : options.filter((o) =>
            o.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()),
          ),
    [inputValue, options],
  );
  return (
    <Combobox
      options={filteredOptions}
      onInputChange={setInputValue}
      onOpen={() => setInputValue("")}
      {...others}
    />
  );
};

const CheckboxField = (
  props: CheckboxProps & {
    id: string;
    labelContent: ReactElement;
    labelHref?: string;
  },
) => {
  const { id, checked, name, labelContent, labelHref, onChange, onBlur } =
    props;

  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
      />
      <label className="text-xs font-medium" htmlFor={id}>
        {React.cloneElement(labelContent, {
          components: {
            a: labelHref ? (
              <Link color="inherit" href={labelHref} target="_blank" />
            ) : null,
          },
        })}
      </label>
    </div>
  );
};

const ErrorMessage = (props: JSX.IntrinsicElements["p"]) => {
  return (
    <p
      className="absolute mt-[2px] text-xs text-red-500"
      role="alert"
      {...props}
    />
  );
};
